/** @file Applies a reviewed plan to a copy of the document for the viewport ghost (never committed). */
import type { AutorigPlan } from '@pose2d/autorig/types';
import { applyBatch } from '@pose2d/commands/applyBatch';
import { lookupCommand } from '@pose2d/commands/lookupCommand';
import { ids } from '@pose2d/format/ids';
import type { Doc } from '@pose2d/format/types';
import { AUTORIG_LIMITS } from './autorigLimits.data';
import type { PlanPreview } from './autorigTypes';

/** A Park-Miller sequence: preview ids are throwaway but must be well formed and repeatable. */
function previewRng(): () => number {
  let seed = 1;
  return () => {
    seed = (seed * 48271) % 2147483647;
    return seed / 2147483647;
  };
}

/**
 * The same `autorig.applyPlan` the server commits, through the real registry; `doc` is left untouched (immer).
 * No validateDoc: the ghost shows what the commit would do, the server refuses what it may not.
 */
export function previewPlan(doc: Doc, plan: AutorigPlan, now: string): PlanPreview {
  if (plan.commands.length === 0) return { kind: 'empty' };
  if (plan.commands.length > AUTORIG_LIMITS.previewMaxCommands) return { kind: 'tooLarge', commands: plan.commands.length };
  const rng = previewRng();
  try {
    const env = { lookup: lookupCommand, now, actor: 'preview', newId: (prefix: Parameters<typeof ids.create>[0]) => ids.create(prefix, rng, 0) };
    return { kind: 'ghost', doc: applyBatch(doc, [{ name: 'autorig.applyPlan', input: { plan } }], env).doc };
  } catch (thrown) {
    return { kind: 'failed', thrown };
  }
}
